import { AppButton } from '@clickguard/design-system'
import { GButton, GInput, GTooltip } from 'components/basic-blocks'
import { ModalTypes } from 'components/basic-blocks/g-modal/types'
import { AppConfig } from 'config'
import {
	useState,
	ChangeEvent,
	FormEvent,
	useCallback,
	useMemo,
	useEffect
} from 'react'
import { useTrackPostHogEvent } from '../../../../../utils/posthog'
import { PostHogEvent, PostHogPath } from '../../../../../utils/posthog-events'
import { FiCopy } from 'react-icons/fi'
import { TbArrowBackUp } from 'react-icons/tb'
import { RiCheckLine } from 'react-icons/ri'
import { useUiStore } from 'store'
import { toast } from 'react-toastify'
import { verifyDnsRecord } from '../../../../../utils/dns-verification'
import { StandardTrackingSection } from './standard-tracking-section'
import { useStores } from '../../../../../hooks'
import { ServerSideTrackingPromo } from './server-side-tracking-promo'
import { Assets } from '../../../../../services/user/assets'
import { getPermissionTier, isValidDomain } from '../../../../../utils'
import { PiWarningDiamondFill } from 'react-icons/pi'
import { ProductAccountDTO } from 'api-models'
import { features } from 'config/features'

export interface ServerSideTrackingState {
	step?: '01' | '02' | 'completed'
	domain?: string
	subdomain?: string
	target?: string
	cnameCreated?: boolean
	setupCompleted?: boolean
	completionDate?: string
}

// Constants
const TOOLTIP_CLASSES = {
	arrow:
		'w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret',
	content:
		'border-none font-grotesk mb-2 bg-t-smoke min-w-max font-medium text-sm text-t-desc-gray'
}

const MANUAL_STEPS = [
	{
		number: 1,
		text: (
			<>
				Go to your{' '}
				<span className="font-semibold">domain's DNS configuration panel;</span>
			</>
		)
	},
	{
		number: 2,
		text: 'Create a new CNAME record with the settings below;'
	},
	{
		number: 3,
		text: (
			<>
				<span className="font-semibold">If you're using Cloudflare</span>, set
				Proxy status to <span className="font-semibold">"DNS only"</span>;
			</>
		)
	},
	{
		number: 4,
		text: (
			<>
				<span className="font-semibold">Save the DNS record.</span> This may
				take some time to propagate (usually 5-30 minutes);
			</>
		)
	},
	{
		number: 5,
		text: (
			<>
				Return to this page and click{' '}
				<span className="font-semibold">"Verify installation"</span>;
			</>
		)
	},
	{
		number: 6,
		text: (
			<>
				If the installation is successful, continue to the next section:{' '}
				<span className="font-semibold">Tracking code installation</span>;
			</>
		)
	}
]

// Sub-components
const EmailDeveloperModal = () => {
	const setModalState = useUiStore((s) => s.setModalState)
	const [email, setEmail] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const trackPostHogEvent = useTrackPostHogEvent()

	const {
		assetStore: { asset },
		accountStore: { account }
	} = useStores()

	const validateEmail = useCallback((email: string) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		return emailRegex.test(email)
	}, [])

	const handleSend = useCallback(async () => {
		setError(null)

		if (!email.trim()) {
			setError('Email is required')
			return
		}

		if (!validateEmail(email)) {
			setError('Please enter a valid email address')
			return
		}

		if (!account?.id || !asset?.id) {
			setError('Account or asset information is missing')
			return
		}

		// Check if DNS configuration exists
		if (
			!asset.server_side_tracking?.subdomain ||
			!asset.server_side_tracking?.target
		) {
			setError('DNS configuration not found. Please generate DNS record first.')
			return
		}

		setIsLoading(true)
		try {
			trackPostHogEvent(
				PostHogEvent.SEND_DNS_EMAIL,
				PostHogPath.SETTINGS_TC,
				null
			)
			await Assets.sendDnsEmail(account.id, asset.id, email)
			toast.success('DNS instructions sent successfully!')
			setModalState(false)
			setEmail('')
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Failed to send email. Please try again.'
			setError(errorMessage)
			toast.error(errorMessage)
			console.error('Error sending DNS email:', err)
		} finally {
			setIsLoading(false)
		}
	}, [email, validateEmail, setModalState, account?.id, asset?.id])

	const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value)
		setError(null)
	}, [])

	return (
		<>
			<p className="text-t-checkbox-unactive text-base mb-4">
				Email the DNS installation instructions to your developer or to your
				domain manager.
			</p>
			<form
				data-hs-do-not-collect="true"
				onSubmit={(e: FormEvent<HTMLFormElement>) => e.preventDefault()}
			>
				<div className="flex flex-col sm:flex-row gap-3 sm:items-end mb-4">
					<div className="w-full sm:flex-1">
						<GInput
							error={error || undefined}
							name="email"
							labelClassName="text-base font-medium"
							className="flex flex-col gap-1"
							inputClassName="w-full text-base font-normal"
							label="Email"
							placeholder="Fill in a valid email"
							errorPosition="flex"
							type="email"
							value={email}
							onChange={handleEmailChange}
						/>
					</div>
					<GButton
						label="Send"
						variant="contained"
						color="yellow"
						labelClassName="font-medium text-base"
						className="h-[40px] px-6 rounded-[8px] min-w-[90px] disabled:!shadow-none !shadow-s-yellow"
						onClick={handleSend}
						loading={isLoading}
						disabled={!email.trim() || isLoading}
					/>
				</div>
			</form>
		</>
	)
}

const StepHeader = ({
	stepNumber,
	title
}: {
	stepNumber: string
	title: string
}) => (
	<div className="mb-4 lg:mb-6">
		<div className="inline-flex items-center px-3 py-1 rounded-full bg-violet-400 text-[11px] font-bold text-t-email-blue mb-3">
			{stepNumber}
		</div>
		<h1 className="text-xl font-medium">{title}</h1>
	</div>
)

const CompletedDnsInstructions = ({
	subdomain,
	domain,
	target
}: {
	subdomain: string
	domain: string
	target: string
}) => {
	return (
		<div className="mb-6">
			<h2 className="text-lg font-medium mb-4">DNS Instructions</h2>
			<div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
				<div className="text-sm font-medium text-t-desc-gray">
					<span className="underline cursor-pointer hover:text-t-base-blue">
						{subdomain}.{domain}
					</span>{' '}
					CNAME{' '}
					<span className="underline cursor-pointer hover:text-t-base-blue">
						{target}
					</span>
				</div>
			</div>
		</div>
	)
}

const StepCompletedStatus = ({
	completionDate,
	onVerifyAgain,
	onReset,
	isVerifying,
	hideVerification = false
}: {
	completionDate: Date
	onVerifyAgain: () => void
	onReset: () => void
	isVerifying: boolean
	hideVerification?: boolean
}) => {
	const formatDate = useCallback((date: Date) => {
		const formatted = date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric'
		})
		// Convert to lowercase (e.g., "nov 18, 2025")
		return formatted.toLowerCase()
	}, [])

	return (
		<div className="mb-6">
			<div className="flex items-center gap-3 mb-4">
				<RiCheckLine className="w-6 h-6 text-green-500" />
				<span className="text-base font-medium text-green-600">
					Step completed at {formatDate(completionDate)}
				</span>
				<div className="flex items-center gap-4 ml-2">
					{!hideVerification && (
						<GButton
							color="neutral"
							className="rounded-[8px] px-4"
							label="Verify again"
							onClick={onVerifyAgain}
							loading={isVerifying}
							disabled={isVerifying}
						/>
					)}
					<GButton
						color="neutral"
						className="rounded-[8px] px-4"
						label="Reset"
						onClick={onReset}
					/>
				</div>
			</div>
		</div>
	)
}

const CopyablePill = ({
	label,
	value,
	copyKey,
	copyStatus,
	onCopy,
	copyValue
}: {
	label: string
	value: string
	copyKey: string
	copyStatus: Record<string, boolean>
	onCopy: (text: string, key: string) => void
	copyValue?: string
}) => (
	<div
		className="relative flex cursor-pointer items-center gap-2 rounded-[8px] border border-t-badge-border bg-t-step-inactive px-[14px] py-[10px]"
		onClick={() => onCopy(copyValue || value, copyKey)}
	>
		<span className="text-[12px] leading-4 text-[#7169ff]">
			{label} = {value}
		</span>
		<GTooltip
			position="top"
			arrowClassName={TOOLTIP_CLASSES.arrow}
			contentClassName={TOOLTIP_CLASSES.content}
			content={copyStatus[copyKey] ? 'Copied!' : 'Copy'}
		>
			{/* Copy → check feedback: the two icons are stacked and cross-fade /
			    scale on copy, then revert when copyStatus resets (Cherry #0015). */}
			<span className="relative inline-flex h-3 w-3 items-center justify-center">
				<FiCopy
					className={`absolute h-3 w-3 text-[#7169ff] transition-all duration-200 hover:text-brand ${
						copyStatus[copyKey]
							? 'scale-0 opacity-0'
							: 'scale-100 opacity-100'
					}`}
				/>
				<RiCheckLine
					className={`absolute h-3 w-3 text-green-500 transition-all duration-200 ${
						copyStatus[copyKey]
							? 'scale-100 opacity-100'
							: 'scale-0 opacity-0'
					}`}
				/>
			</span>
		</GTooltip>
	</div>
)

const ManualModeInstructions = ({
	cnameRecord,
	nameValue,
	targetValue,
	ttlValue,
	copyStatus,
	onCopy,
	onVerify,
	onEmailDeveloper,
	isVerifying,
	hideVerification = false
}: {
	cnameRecord: string
	nameValue: string
	targetValue: string
	ttlValue: string
	copyStatus: Record<string, boolean>
	onCopy: (text: string, key: string) => void
	onVerify: () => void
	onEmailDeveloper: () => void
	isVerifying: boolean
	hideVerification?: boolean
}) => (
	<div className="mb-6">
		<h2 className="text-lg font-medium mb-4">DNS Instructions</h2>
		<div className="bg-bg-plan-fill border border-t-radio rounded-lg p-4 mb-6">
			<p className="text-sm font-medium text-t-desc-gray">{cnameRecord}</p>
		</div>

		<div className="relative mb-8">
			{/* Vertical line connecting step numbers - positioned at center of circles */}
			<div className="absolute left-[11px] top-[12px] bottom-[12px] w-0.5 bg-violet-400"></div>
			<ol className="space-y-4 text-sm text-t-default relative !p-0 !list-none">
				{MANUAL_STEPS.map((step, index) => (
					<li
						key={step.number}
						className="flex items-start gap-3 relative z-10"
					>
						<span className="flex items-center justify-center w-6 h-6 rounded-full bg-violet-400 text-xs font-bold text-t-email-blue relative z-10">
							{step.number}
						</span>
						{index === 1 ? (
							<div className="flex-1">
								<span className="block mb-3">{step.text}</span>
								<div className="flex flex-wrap gap-2">
									<CopyablePill
										label="Name"
										value={nameValue}
										copyKey="name"
										copyStatus={copyStatus}
										onCopy={onCopy}
									/>
									<CopyablePill
										label="Target"
										value={targetValue}
										copyKey="target"
										copyStatus={copyStatus}
										onCopy={onCopy}
									/>
									<CopyablePill
										label="TTL"
										value={ttlValue}
										copyKey="ttl"
										copyStatus={copyStatus}
										onCopy={onCopy}
										copyValue="14400"
									/>
								</div>
							</div>
						) : (
							<span>{step.text}</span>
						)}
					</li>
				))}
			</ol>
		</div>

		<div className="flex flex-wrap gap-4 mb-6">
			{!hideVerification && (
				<GButton
					color="neutral"
					className="rounded-[8px] px-6 h-[40px]"
					label="Verify installation"
					onClick={onVerify}
					loading={isVerifying}
					disabled={isVerifying}
				/>
			)}
			<AppButton
				variant="secondary"
				size="md"
				label="Send to developer"
				onClick={onEmailDeveloper}
			/>
		</div>
	</div>
)

const KnowledgeBaseLink = ({ onClick }: { onClick: () => void }) => (
	<p className="text-t-base-blue mb-8">
		Visit the{' '}
		<a
			className="mb-2 text-md text-t-email-blue hover:text-t-dark-blue font-bold"
			href={
				'https://docs.clickguard.com/en/articles/21547-serverside-tracking-setup-process'
			}
			target="_blank"
			onClick={onClick}
		>
			Knowledge base
		</a>{' '}
		for additional help.
	</p>
)

// Main component
export const AdvancedTrackingSection = ({
	hideWarning = false,
	canSkipServerSideTracking = false,
	hideVerification = false,
	onVerificationSuccess
}: {
	hideWarning?: boolean
	canSkipServerSideTracking?: boolean
	hideVerification?: boolean
	onVerificationSuccess?: () => void
}) => {
	const {
		assetStore: { asset, setAssetForId },
		accountStore: { hasFeature, account }
	} = useStores()

	// Get server_side_tracking state from asset (if exists)
	const savedState = asset?.server_side_tracking as
		| ServerSideTrackingState
		| undefined

	// Initialize state from saved state or defaults
	const [domain, setDomain] = useState(
		savedState?.domain || asset?.domain || 'clientwebsite.com'
	)
	const [subdomain, setSubdomain] = useState(savedState?.subdomain || '')
	const [target, setTarget] = useState(
		savedState?.target ||
			(AppConfig.ENV === 'production'
				? 'pulse.clickguard.com'
				: 'tracker-staging.clickguard.com')
	)
	const [cnameCreated, setCnameCreated] = useState(
		savedState?.cnameCreated || false
	)
	const [copyStatus, setCopyStatus] = useState<Record<string, boolean>>({})
	const [step01Completed, setStep01Completed] = useState(
		savedState?.setupCompleted || false
	)
	const [completionDate, setCompletionDate] = useState<Date | null>(
		savedState?.completionDate ? new Date(savedState.completionDate) : null
	)
	const [isVerifying, setIsVerifying] = useState(false)
	const [isUpdatingDomain, setIsUpdatingDomain] = useState(false)
	const [domainError, setDomainError] = useState<string | null>(null)
	const [skipServerSideTracking, setSkipServerSideTracking] = useState(false)
	const setModalContent = useUiStore((s) => s.setModalContent)
	const trackPostHogEvent = useTrackPostHogEvent()
	const hasAccessToServerSideTracking = (
		account: ProductAccountDTO | null
	): boolean => {
		if (!account) return false
		let plan = account?.billing?.subscription?.plan
		if (account?.billing?.subscription?.type === 'TRIAL') {
			plan =
				account?.billing?.subscription?.next_plan ||
				account?.billing?.subscription?.plan
		}

		const tier = getPermissionTier(plan?.tier)
		if (!tier) return false
		if (!features['server_side_tracking']) return false
		if (tier === 'legacy') return !!features['server_side_tracking']['standard']

		return !!features['server_side_tracking'][tier]
	}
	const hasAccess = hasAccessToServerSideTracking(account)

	// Load state from asset on mount
	useEffect(() => {
		if (savedState) {
			if (savedState.domain) setDomain(savedState.domain)
			else setDomain(asset?.domain || '')
			if (savedState.subdomain) setSubdomain(savedState.subdomain)
			else setSubdomain('')
			if (savedState.target) setTarget(savedState.target)
			else
				setTarget(
					AppConfig.ENV === 'production'
						? 'pulse.clickguard.com'
						: 'tracker-staging.clickguard.com'
				)
			if (savedState.cnameCreated !== undefined)
				setCnameCreated(savedState.cnameCreated)
			else setCnameCreated(false)
			if (savedState.setupCompleted !== undefined)
				setStep01Completed(savedState.setupCompleted)
			else setStep01Completed(false)
			if (savedState.completionDate)
				setCompletionDate(new Date(savedState.completionDate))
			else setCompletionDate(null)
		} else {
			setDomain(asset?.domain || '')
			setSubdomain('')
			setTarget(
				AppConfig.ENV === 'production'
					? 'pulse.clickguard.com'
					: 'tracker-staging.clickguard.com'
			)
			setCnameCreated(false)
			setCopyStatus({})
			setStep01Completed(false)
			setCompletionDate(null)
			setIsVerifying(false)
			setIsUpdatingDomain(false)
			setDomainError(null)
		}
	}, [asset?.id, savedState, asset?.domain])

	// Save state to backend
	const saveServerSideTrackingState = useCallback(
		async (
			updates: Partial<ServerSideTrackingState>,
			currentDomain?: string
		) => {
			if (!asset?.id || !account?.id) return

			try {
				const currentState = (asset as any)?.server_side_tracking || {}
				const newState: ServerSideTrackingState = {
					...currentState,
					...updates
				}

				// Use provided domain or current domain state
				const domainToSave = currentDomain || domain
				await Assets.updateAssetDomain(
					account.id,
					asset.id,
					domainToSave,
					newState
				)
				await setAssetForId(account.id, asset.id)
			} catch (error) {
				console.error('Failed to save server-side tracking state:', error)
			}
		},
		[asset?.id, account?.id, domain, setAssetForId]
	)

	// Memoized values
	const cnameRecord = useMemo(
		() => `${subdomain}.${domain} CNAME ${target}`,
		[subdomain, domain, target]
	)
	const nameValue = useMemo(() => subdomain, [subdomain])
	const targetValue = useMemo(() => target, [target])
	const ttlValue = '14400 (or your default TTL)'

	// Domain validation
	const validateDomain = useCallback((domainValue: string): boolean => {
		if (!domainValue || domainValue.trim().length === 0) {
			setDomainError('Domain is required')
			return false
		}
		if (!isValidDomain(domainValue)) {
			setDomainError('Please enter a valid domain (e.g., example.com)')
			return false
		}
		setDomainError(null)
		return true
	}, [])

	// Handle domain change
	const handleDomainChange = useCallback(
		(newDomain: string) => {
			setDomain(newDomain)
			validateDomain(newDomain)
			// Save domain change to server_side_tracking state
			if (isValidDomain(newDomain)) {
				saveServerSideTrackingState({ domain: newDomain }, newDomain)
			}
		},
		[validateDomain, saveServerSideTrackingState]
	)

	// Handlers
	const handleCreateCname = useCallback(async () => {
		if (!asset?.id || !account?.id) {
			toast.error('Asset or account information is missing')
			return
		}

		// Validate domain
		if (!validateDomain(domain)) {
			return
		}

		setIsUpdatingDomain(true)
		try {
			// Update the asset domain and request new subdomain from backend
			// Backend will generate a random subdomain and return it in server_side_tracking
			const serverSideTrackingState: ServerSideTrackingState = {
				domain,
				step: '01',
				cnameCreated: true
			}

			const updatedAsset = await Assets.updateAssetDomain(
				account.id,
				asset.id,
				domain,
				serverSideTrackingState
			)

			// Get the subdomain and target from backend response
			const responseState = (updatedAsset as any)?.server_side_tracking as
				| ServerSideTrackingState
				| undefined

			// Only show DNS instructions after we have confirmed the subdomain from backend
			if (responseState?.subdomain) {
				// Refresh the asset in the store
				await setAssetForId(account.id, asset.id)
				toast.success('DNS record generated successfully')
			} else {
				throw new Error('Failed to generate subdomain from backend')
			}
		} catch (error: any) {
			console.error('Failed to generate DNS record:', error)
			toast.error(
				error?.response?.data?.message ||
					error?.message ||
					'Failed to generate DNS record. Please try again.'
			)
		} finally {
			// Hide loader after all operations complete
			setIsUpdatingDomain(false)
		}
	}, [domain, asset?.id, account?.id, setAssetForId, validateDomain])

	const handleRegenerate = useCallback(async () => {
		if (!asset?.id || !account?.id) {
			toast.error('Asset or account information is missing')
			return
		}
		setCnameCreated(false)
		try {
			// Request a new random subdomain from backend
			const serverSideTrackingState: ServerSideTrackingState = {
				domain,
				step: '01',
				cnameCreated: false // Reset to request new subdomain
			}

			await Assets.updateAssetDomain(
				account.id,
				asset.id,
				domain,
				serverSideTrackingState
			)

			// Refresh the asset in the store
			await setAssetForId(account.id, asset.id)
		} catch (error: any) {
			console.error('Failed to regenerate DNS record:', error)
		}
	}, [domain, asset?.id, account?.id, setAssetForId])

	const copyToClipboard = useCallback(async (text: string, key: string) => {
		await navigator.clipboard.writeText(text)
		setCopyStatus((prev) => ({ ...prev, [key]: true }))
		setTimeout(() => {
			setCopyStatus((prev) => ({ ...prev, [key]: false }))
		}, 2000)
	}, [])

	const handleVerifyInstallation = useCallback(async () => {
		if (!validateDomain(domain)) {
			return
		}

		if (!asset?.id || !account?.id) {
			toast.error('Asset or account information is missing')
			return
		}

		setIsVerifying(true)
		try {
			const result = await verifyDnsRecord(account.id, asset.id)
			if (result.valid) {
				await setAssetForId(account.id, asset.id)
				toast.success('DNS verification successful!')
			} else {
				toast.error('DNS verification failed. Please check your DNS settings.')
			}
		} catch (error) {
			toast.error('Error verifying DNS record. Please try again.')
			console.error('DNS verification error:', error)
		} finally {
			setIsVerifying(false)
		}
	}, [
		domain,
		validateDomain,
		saveServerSideTrackingState,
		asset?.id,
		account?.id
	])

	const handleVerifyAgain = useCallback(() => {
		handleVerifyInstallation()
	}, [handleVerifyInstallation])

	const handleReset = useCallback(async () => {
		setStep01Completed(false)
		setCompletionDate(null)
		await saveServerSideTrackingState({
			step: '01',
			setupCompleted: false,
			completionDate: undefined
		})
	}, [saveServerSideTrackingState])

	const handleEmailDeveloper = useCallback(() => {
		setModalContent({
			title: 'Send the instructions by email',
			type: ModalTypes.INFO,
			actionsEnabled: false,
			showIcon: false,
			body: <EmailDeveloperModal />
		})
	}, [setModalContent])

	const handleKnowledgeBaseClick = useCallback(() => {
		trackPostHogEvent(
			PostHogEvent.LINK_KNOWLEDGE_BASE,
			PostHogPath.SETTINGS_TC,
			null
		)
	}, [trackPostHogEvent])

	// TODO: Replace with actual check for server-side tracking availability
	const showServerSideTrackingPromo = !hasAccess

	if (showServerSideTrackingPromo) {
		return <ServerSideTrackingPromo />
	}

	return (
		<div>
			<StepHeader stepNumber="Step 01" title="Server-side tracking" />
			<p className="text-t-checkbox-unactive text-base mb-6">
				{!skipServerSideTracking
					? 'Advanced click tracking solution to ensure ClickGuard can track every click and guarantee full protection.'
					: 'Bypasses ad blockers and browser tracking limitations, so we can track every click and guarantee full protection.'}
			</p>
			<p className="text-t-base-blue mt-6">
				Visit the{' '}
				<a
					className="mb-2 text-md text-t-email-blue hover:text-t-dark-blue font-bold"
					href={
						'https://docs.clickguard.com/en/articles/21547-serverside-tracking-setup-process'
					}
					target="_blank"
					onClick={() => {
						trackPostHogEvent(
							PostHogEvent.LINK_KNOWLEDGE_BASE,
							PostHogPath.SETTINGS_TC,
							null
						)
					}}
				>
					Knowledge base
				</a>{' '}
				for additional help.
			</p>
			{skipServerSideTracking && (
				<GButton
					color="neutral"
					className="rounded-[8px] px-6 h-[40px]"
					icon={TbArrowBackUp}
					iconPlacement="left"
					label="back"
					onClick={() => {
						setStep01Completed(false)
						setSkipServerSideTracking(false)
					}}
				/>
			)}

			{step01Completed && completionDate && (
				<>
					<CompletedDnsInstructions
						subdomain={subdomain}
						domain={domain}
						target={target}
					/>
					<StepCompletedStatus
						completionDate={completionDate}
						onVerifyAgain={handleVerifyAgain}
						onReset={handleReset}
						isVerifying={isVerifying}
						hideVerification={hideVerification}
					/>
				</>
			)}

			{!step01Completed && (
				<>
					<hr className="my-6 border-t border-gray-200" />

					<div className="mb-8">
						<div
							className={`flex flex-col sm:flex-row gap-3 mb-6 ${
								domainError ? 'items-center' : 'items-end'
							}`}
						>
							<div className="w-full sm:max-w-[420px]">
								<GInput
									label="Domain"
									labelClassName="text-base font-medium"
									className="flex flex-col gap-1"
									inputClassName="w-full text-base font-normal"
									value={domain}
									onChange={(e) => handleDomainChange(e.target.value)}
									placeholder="clientwebsite.com"
									disabled={cnameCreated}
									error={domainError || undefined}
								/>
							</div>
							{!cnameCreated && (
								<GButton
									color="yellow"
									className="rounded-[8px] px-6 h-[40px] min-w-[200px]"
									label="Generate DNS record"
									onClick={handleCreateCname}
									disabled={cnameCreated || isUpdatingDomain}
									loading={isUpdatingDomain}
								/>
							)}
							{cnameCreated && !isUpdatingDomain && (
								<AppButton
									variant="secondary"
									size="md"
									label="Regenerate"
									onClick={handleRegenerate}
								/>
							)}
						</div>
						{!cnameCreated && canSkipServerSideTracking && (
							<div>
								<GButton
									color="neutral"
									className="underline rounded-full px-6 min-w-[150px] p-0 border-0 border-t-0 border-r-0 border-b-0 border-l-0 shadow-none hover:bg-transparent"
									label="I don't want to use server-side tracking"
									onClick={() => {
										setStep01Completed(true)
										setSkipServerSideTracking(true)
									}}
								/>
							</div>
						)}
					</div>
					{cnameCreated && !isUpdatingDomain && (
						<>
							<ManualModeInstructions
								cnameRecord={cnameRecord}
								nameValue={nameValue}
								targetValue={targetValue}
								ttlValue={ttlValue}
								copyStatus={copyStatus}
								onCopy={copyToClipboard}
								onVerify={handleVerifyInstallation}
								onEmailDeveloper={handleEmailDeveloper}
								isVerifying={isVerifying}
								hideVerification={hideVerification}
							/>
							<KnowledgeBaseLink onClick={handleKnowledgeBaseClick} />
						</>
					)}
				</>
			)}

			{step01Completed &&
				asset?.server_side_tracking?.verification?.eventReceived !== true &&
				!hideWarning && (
					<div className="mb-2 flex gap-3 items-center">
						<span className="text-[#FABC04]">
							<PiWarningDiamondFill className="w-6 h-6" />
						</span>
						<p className="text-md text-t-checkbox-unactive">
							Please complete step 2 to update your current ClickGuard tracking
							code. This update ensures that server-side tracking functions
							correctly.
						</p>
					</div>
				)}

			<hr className="my-6 border-t border-gray-200" />

			<div className="mt-4">
				<div className="inline-flex items-center px-3 py-1 rounded-full bg-violet-400 text-[11px] font-bold text-t-email-blue mb-3">
					Step 02
				</div>

				{!step01Completed ? (
					<>
						<h2 className="text-lg font-medium mb-2">
							Tracking code installation
						</h2>
						<p className="text-t-checkbox-unactive text-base">
							Instructions will be available after completing step 01 –
							Server-side tracking.
						</p>
					</>
				) : (
					<StandardTrackingSection
						onVerificationSuccess={onVerificationSuccess}
						hideVerification={hideVerification}
					/>
				)}
			</div>
		</div>
	)
}
